import { GetServerSideProps } from "next";
import React, { useEffect, useState } from "react";
import Link from "next/link";
import ErrorScreen from "@/components/ui/error-screen";
import { AppBtn } from "@/components/ui/buttons";
import { generateComplementaryHexColor3, hexToRgb, isValidHexCode, reloadPage } from "@/assets/js/utils/functions";
import SEOTags from "@/components/seo-tags";
import useAnalytics, { PAGE_TYPE } from "@/components/hooks/analytics";
import CartContext from "@/contexts/cart-context";
import ProductPageDetails from "@/components/products/product-page-details";
import { AccountInformation, ProductItemInterface, StoreInterface } from "@/assets/interfaces";
import router from "next/router";
import { useModals } from "@/components/hooks/useModals";
import LazyImage from "@/components/lazy-image";
import useSlider from "@/components/hooks/useSlider";
import Portal from "@/components/portal";
import ImageCarouselModal from "@/components/products/image-carousel-modal";
import { getWhatsappLink } from "@/assets/js/utils/utils";
import { useFetcher, useRequest } from "@/api/utils";
import { GetStoreBankAccount } from "@/api/stores";
import ProductPageHeader from "@/components/products/header-old";
import { ProductPageProps } from "@/components/storefronts-v2/product-page";

const ProductPageV1: React.FC<ProductPageProps> = ({ item, error, query }) => {
  const { saveAnalytics } = useAnalytics(PAGE_TYPE.ITEM, query?.ref);
  const store = error ? null : item?.store;
  const storeId = error ? null : store?.id;
  const { modals, toggleModal } = useModals(["image_carousel"]);
  const [currentImage, setCurrentImage] = useState(0);
  const getStoreBankAccountReq = useFetcher(GetStoreBankAccount, { id: storeId });
  const storeBankAccount: AccountInformation = getStoreBankAccountReq?.response?.data;

  const showFullImage = (index: number) => {
    setCurrentImage(index);
    toggleModal("image_carousel");
  };

  useEffect(() => {
    if (!error) {
      saveAnalytics({ store: storeId, item: item.id, itemName: item.name });
      if (item.store) {
        window.COUNTRY = item.store.country;

        if (store?.configuration?.color && isValidHexCode(store?.configuration?.color)) {
          const p = hexToRgb(store?.configuration?.color);
          const primaryRGB = `${p.r}, ${p.g}, ${p.b}`;
          document.documentElement.style.setProperty("--primary-500", primaryRGB);
          document.documentElement.style.setProperty("--discount-badge", primaryRGB);

          const s = hexToRgb(generateComplementaryHexColor3(store?.configuration?.color));
          const secondaryRGB = `${s.r}, ${s.g}, ${s.b}`;
          document.documentElement.style.setProperty("--accent-yellow-500", secondaryRGB);
          document.documentElement.style.setProperty("--add-to-cart", secondaryRGB);
          document.documentElement.style.setProperty("--success", secondaryRGB);
          document.documentElement.style.setProperty("--cart-badge", secondaryRGB);
        }
      }
    }
  }, []);

  if (error) {
    return (
      <ErrorScreen
        title="Something went wrong :("
        message="We couldn't load this item, check your network and try reloading the page"
      >
        <AppBtn className="mt-5" size="md" onClick={reloadPage}>
          Reload page
        </AppBtn>
      </ErrorScreen>
    );
  }

  return (
    <>
      <SEOTags
        tags={{
          title: `${item.name} | ${item.store.name} - Catlog`,
          description: item.description,
          image: item.images[item.thumbnail],
          pageUrl: `/products/${item.slug}`,
        }}
      />
      <CartContext.Provider
        initialState={{
          storeId: storeId,
          cartRef: `p-${item.id}`,
          account: storeBankAccount,
          currencySettings: store?.currencies
            ? {
                active: store?.currencies?.storefront_default,
                rates: store?.currencies?.rates,
                options: store?.currencies?.storefront,
                defaultCurrency: store?.currencies?.products,
                allRates: store["meta"]?.["rates"]?.rates ?? {},
                rateId: store["meta"]?.["rates"]?.id ?? "",
                markups: store?.currencies?.rates,
              }
            : undefined,
        }}
      >
        <div className="relative md:h-screen md:overflow-hidden flex flex-col">
          <ProductPageHeader store={store} />
          <div className="md:grid md:grid-cols-[1fr,400px] lg:grid-cols-[1fr,500px] w-full md:flex-1 md:overflow-hidden mt-16 sm:mt-17.5 md:mt-[75px]">
            <div className="bg-accent-yellow-pastel p-7.5 sm:p-12.5 md:!p-10 lg:!p-20 w-full overflow-hidden md:h-full">
              <ProductImages {...{ showFullImage, images: item?.images }} />
            </div>
            <div className="relative pb-20 h-full overflow-hidden">
              <ProductPageDetails item={item} />
            </div>
          </div>
        </div>
        {item?.images && (
          <Portal>
            <ImageCarouselModal
              show={modals.image_carousel}
              toggle={() => toggleModal("image_carousel")}
              images={item?.images}
              current={currentImage}
            />
          </Portal>
        )}
      </CartContext.Provider>
    </>
  );
};

export const ProductImages: React.FC<{ showFullImage: (index: number) => void; images: string[] }> = ({
  showFullImage,
  images,
}) => {
  const itemsCount = images.length;
  const { next, slider, slide, previous, slides, currentSlide, switchSlides, hasNext, hasPrevious } = useSlider({
    slides: itemsCount,
    sameWidthAsSlider: true,
    gap: 0,
  });

  return (
    <div className="h-full w-full max-w-[900px] mx-auto overflow-hidden relative">
      <ul
        className="grid w-full h-full overflow-hidden max-w-[400px] md:max-w-full mx-auto"
        style={{ gridTemplateColumns: `repeat(${images?.length}, 100%)` }}
        ref={slider}
      >
        {images.map((image, index) => (
          <figure
            key={index}
            className="w-full relative overflow-hidden pt-[100%] md:pt-0 h-full flex items-center justify-center rounded-15 safari-overflow-fix"
            style={{ cursor: "zoom-in" }}
            onClick={() => showFullImage(index)}
            ref={slide}
          >
            <LazyImage
              src={image}
              alt=""
              className="w-full h-full md:h-auto md:w-auto md:max-w-full md:max-h-full object-cover absolute md:relative top-0 left-0 z-10 rounded-15"
            />
          </figure>
        ))}
      </ul>
      <div className="flex items-center w-full justify-between absolute top-1/2 transform -translate-y-1/2 z-50">
        {hasPrevious && (
          <button
            className="h-10 sm:h-12.5 w-10 sm:w-12.5 rounded-full bg-primary-500 flex items-center justify-center shadow-lg text-white"
            onClick={previous}
          >
            {/* prettier-ignore */}
            <svg width="30%" viewBox="0 0 14 14" fill="none">
              <path d="M13 7H1" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M7 1L1 7L7 13" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
        )}
        {hasNext && (
          <button
            className="h-10 sm:h-12.5 w-10 sm:w-12.5 rounded-full text-white flex items-center justify-center shadow-lg bg-primary-500 ml-auto"
            onClick={next}
          >
            {/* prettier-ignore */}
            <svg width="30%" viewBox="0 0 12 12" fill="none" >
              <path d="M6 0L4.9425 1.0575L9.1275 5.25H0V6.75H9.1275L4.9425 10.9425L6 12L12 6L6 0Z" fill="currentColor"/>
            </svg>
          </button>
        )}
      </div>
      {itemsCount > 1 && (
        <div className="absolute bottom-5 bg-black bg-opacity-70 text-white text-1xs font-medium px-2.5 py-1 flex items-center justify-center rounded-30 left-1/2 transform -translate-x-1/2 z-50 ">
          {currentSlide + 1} of {itemsCount} Images
        </div>
      )}
    </div>
  );
};

export const getServerSideProps: GetServerSideProps = async (context) => {
  const slug = context.params.slug as string;
  const query = context.query;

  try {
    const res = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/items/public/${slug}`);

    if (res.ok) {
      const data = await res.json();

      if (!data.data) {
        //switch to custom not-found
        return {
          notFound: true,
        };
      }

      return {
        props: {
          item: data.data,
          error: false,
          query,
        },
      };
    }

    if (res.status === 404) {
      return {
        notFound: true,
      };
    }

    throw Error;
  } catch (e) {
    return {
      props: {
        error: true,
        item: null,
      },
    };
  }
};

export default ProductPageV1;
